/**
 * The voice call, from anywhere in the window (RFC 0307), at the right end of
 * the space tab strip.
 *
 * A call outlives the pane it started in, so when its session is not on
 * screen it needs somewhere to be seen and stopped: who the call is with, the
 * person's level, mute, and end. A click on the name opens the session.
 * Hidden while the session is on screen, where its own composer carries the
 * same controls.
 */

import { AudioLines, Mic, MicOff } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { sessionCalls } from '../../features/thread/sessionCall'
import { levelBars } from '../../features/thread/VoiceControls'
import { SessionMark } from '../../services/sessions/SessionMark'

export function CallPill({
  isOnScreen,
  onOpen,
}: {
  /** Whether a pane in this space already shows the session. */
  isOnScreen: (sessionId: string) => boolean
  onOpen: (sessionId: string) => void
}) {
  const call = useSyncExternalStore(
    sessionCalls.subscribe,
    sessionCalls.current,
  )
  if (!call || isOnScreen(call.sessionId)) return null
  const { voice } = call
  const live = voice.status === 'live'
  return (
    <div className="pointer-events-auto flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-background py-0.5 pr-0.5 pl-2.5 text-xs">
      <button
        type="button"
        onClick={() => onOpen(call.sessionId)}
        className="flex min-w-0 items-center gap-1.5 text-foreground/80 hover:text-foreground"
        title={`Call with ${call.title}: open the session`}
      >
        <AudioLines
          className="size-3.5 shrink-0"
          strokeWidth={2.25}
          aria-hidden
        />
        {voice.status === 'error' ? (
          <span className="max-w-[200px] truncate">
            {voice.error ?? 'Call failed'}
          </span>
        ) : (
          // "Call with" and the session's own mark say the name is WHO the
          // call is with, not the call's state: a session named "stopped"
          // beside a live mic read as a call that had stopped.
          <>
            <span className="text-muted-foreground">
              {voice.status === 'connecting' ? 'Connecting to' : 'Call with'}
            </span>
            <SessionMark id={call.sessionId} size={14} />
            <span className="max-w-[160px] truncate">{call.title}</span>
          </>
        )}
      </button>
      <button
        type="button"
        onClick={voice.toggleMic}
        disabled={!live}
        aria-label={voice.micMuted ? 'Unmute your mic' : 'Mute your mic'}
        className={`flex h-6 items-center gap-1.5 rounded-full border border-border px-2 hover:bg-muted ${
          voice.micMuted ? 'text-muted-foreground' : 'text-foreground'
        }`}
      >
        <span className="flex items-center gap-[2px]" aria-hidden>
          {levelBars(voice.micMuted || !live ? 0 : voice.level)
            .slice(0, 4)
            .map((h, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: four fixed bars
                key={i}
                className={`w-[2px] rounded-full ${
                  voice.micMuted ? 'bg-muted-foreground/50' : 'bg-blue-500'
                }`}
                style={{ height: Math.min(h, 12) }}
              />
            ))}
        </span>
        {voice.micMuted ? (
          <MicOff className="size-3.5" aria-hidden />
        ) : (
          <Mic className="size-3.5" aria-hidden />
        )}
      </button>
      <button
        type="button"
        onClick={sessionCalls.end}
        aria-label="End call"
        title="End call"
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground/[0.12] text-foreground hover:bg-foreground/[0.18]"
      >
        <span className="size-2 rounded-[2px] bg-current" />
      </button>
    </div>
  )
}
